import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { recordarTransito } from '../../lib/transito';
import { useStore } from '../../store/useStore';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { toast } from 'sonner';
import { fmtDate, dateGroup } from '../../lib/format';
import { describirErrorRed, mensajeErrorRed } from '../../lib/erroresRed';
import { VistoBurbuja } from './Visto';
import { filterByCategory } from '../../lib/mailCategories';
import SenderAvatar from './SenderAvatar';
import ConfirmarBorradoDefinitivo, { avisoEliminados } from './BorradoDefinitivo';
import { DialogoConfirmar } from '../ui/confirmar';
import { contadorDeCarpeta, peticionDeCarpeta, vacioDePestana } from '../../lib/carpetaCorreo';
import { alcanceBusqueda } from '../../lib/alcanceBusqueda';
import ConteoPestana from './ConteoPestana';
import AvisoPosibleSpam from './AvisoPosibleSpam';
import { avisoArchivado, marcarArchivadas } from '../../lib/trasMover';
import { textoProgramado } from '../../lib/programar';
import {
  Star, Paperclip, Archive, Trash2, RefreshCw, Loader2, Inbox,
  Layers, ChevronRight, ChevronDown, Mail, MailOpen, Tag, AlertOctagon,
  Clock, AlarmClock, TimerReset, CalendarClock, Send, X as XIcon, BookmarkPlus,
  Check, AlertTriangle, CloudOff, Search, Bell,
} from 'lucide-react';

/* Tamaño de página de la lista: 50, el mismo en el portal y en la app, y el que
   el backend usa por defecto. Se piden más al llegar al final. */
const PAGINA = 50;

const FOLDER_LABELS = {
  inbox: 'Recibidos', sent: 'Enviados', drafts: 'Borradores',
  all: 'Todos', archive: 'Archivo', spam: 'Spam', trash: 'Papelera',
  starred: 'Destacados', snoozed: 'Pospuestos', scheduled: 'Programados',
  needs_reply: 'Por responder',
};

/* El backend devuelve el estado en inglés; aquí se muestra en español. */
const ESTADO_PROGRAMADO = {
  scheduled: 'Programado', retry: 'Reintentando', sent: 'Enviado',
  failed: 'Falló', cancelled: 'Cancelado',
};

/* Programados y pospuestos son la misma idea —correo que aún no toca— así que
   comparten vista y se cambia entre ellos con estas pestañas, sin volver a la
   barra lateral. */
const TRANSITO = [
  { id: 'scheduled', label: 'Programados', icon: CalendarClock },
  { id: 'snoozed', label: 'Pospuestos', icon: Clock },
];

/* Las categorías las calcula el BACKEND al indexar cada correo (mail_index) y
   filtra en la propia consulta, así "cargar más" pagina dentro de la pestaña.
   Ver `category=` en /mail/unified. */
const TABS = [
  { id: 'primary', label: 'Principales', short: 'Principal', icon: Mail },
  { id: 'updates', label: 'Novedades', short: 'Novedades', icon: Bell },
  { id: 'promotions', label: 'Promociones', short: 'Promo', icon: Tag },
  { id: 'social', label: 'Social', short: 'Social', icon: Layers },
  { id: 'needs_reply', label: 'Por responder', short: 'Resp.', icon: AlertOctagon },
];

function normSubject(s) {
  return (s || '').replace(/^((re|fwd|rv|fw):\s*)+/i, '').trim().toLowerCase();
}

/* Clave de conversación.

   El backend calcula `thread_id` siguiendo References/In-Reply-To (RFC 5322),
   que es la cadena que el propio correo lleva encima. El asunto solo se usa
   cuando esa cadena no existe: correos indexados antes de que hubiera
   enhebrado, o la rama en vivo contra el proveedor. Agrupar por asunto juntaba
   "Reunion" de dos personas distintas y separaba una respuesta con el asunto
   cambiado; con thread_id eso deja de pasar. */
function threadKey(m) {
  if (m?.thread_id) return `t:${m.thread_id}`;
  return `s:${normSubject(m?.subject)}`;
}

/* Checkbox del diseño: cuadro 18px que se llena de acento con un check. */
function RowCheckbox({ checked, onChange, label = 'Seleccionar', className = '', testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      aria-label={label}
      data-testid={testId}
      className={`gmail-checkbox ${checked ? 'is-checked' : ''} ${className}`}
      onClick={(e) => { e.stopPropagation(); onChange?.(!checked); }}
    >
      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
    </button>
  );
}

/* Cuánto hay que deslizar una fila para archivar/eliminar: ~35 % del ancho, nunca menos de 100 px. */
const umbralDeslizar = () => Math.max(100, (typeof window !== 'undefined' ? window.innerWidth : 400) * 0.35);

function MessageRow({ m, selected, expanded, mobile, showAccount, mostrarArchivado, onClick, onAction, checked, onCheck, internalDomains, definitivo }) {
  const [dx, setDx] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const active = useRef(false);
  const locked = useRef(false);
  const cruzado = useRef(false);

  const onTouchStart = (e) => {
    if (!mobile) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    active.current = true;
    locked.current = false;
    setSwiping(true);
  };
  const onTouchMove = (e) => {
    if (!mobile || !active.current) return;
    const d = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!locked.current) {
      if (Math.abs(d) > 10 && Math.abs(d) > Math.abs(dy)) locked.current = true;
      else if (Math.abs(dy) > 10) { active.current = false; setSwiping(false); return; }
    }
    if (locked.current) {
      const tope = window.innerWidth * 0.6;
      const nuevo = Math.max(-tope, Math.min(tope, d));
      // Un toque de vibración al cruzar el umbral: avisa de que soltar ya ejecuta la acción.
      const pasa = Math.abs(nuevo) >= umbralDeslizar();
      if (pasa !== cruzado.current) {
        cruzado.current = pasa;
        if (pasa) { try { navigator.vibrate?.(12); } catch (_) { /* sin vibración */ } }
      }
      setDx(nuevo);
    }
  };
  const onTouchEnd = () => {
    if (!mobile) return;
    active.current = false;
    setSwiping(false);
    // Umbral relativo al ancho (M13): con 80 px fijos, un desliz casual al hacer scroll archivaba.
    if (dx >= umbralDeslizar()) onAction('archive');
    else if (dx <= -umbralDeslizar()) onAction('trash');
    cruzado.current = false;
    setDx(0);
  };

  const isChild = m._child;
  const isThread = m._isThread;
  const fromLabel = m.from?.name || m.from?.email || '(desconocido)';
  const preview = m.ai_one_line || m.preview || m.snippet || '';

  return (
    <div className="relative">
      {mobile && (
        <>
          <div
            className="absolute inset-0 flex items-center pl-5"
            style={{ background: 'var(--good)', color: '#fff', opacity: dx > 0 ? 1 : 0 }}
          >
            <Archive className="h-5 w-5" /><span className="ml-2 text-sm">Archivar</span>
          </div>
          <div
            className="absolute inset-0 flex items-center justify-end pr-5"
            style={{ background: 'var(--danger)', color: '#fff', opacity: dx < 0 ? 1 : 0 }}
          >
            <span className="mr-2 text-sm">{definitivo ? 'Eliminar para siempre' : 'Eliminar'}</span><Trash2 className="h-5 w-5" />
          </div>
        </>
      )}
      <div
        data-testid={isChild ? 'mail-thread-child' : 'mail-message-row'}
        onClick={onClick}
        // Usable con teclado (M16): Tab llega a la fila y Enter/Espacio la abre. Solo si el foco
        // está en la fila misma: Enter en la estrella o la casilla es de esos botones.
        tabIndex={0}
        aria-current={selected ? 'true' : undefined}
        aria-expanded={isThread ? !!expanded : undefined}   // B10: Enter despliega el hilo; se anuncia
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); }
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={mobile ? { transform: `translateX(${dx}px)`, transition: swiping ? 'none' : 'transform 0.2s ease' } : undefined}
        className={[
          'gmail-row group',
          mobile ? 'gmail-row--mobile' : '',
          m.seen ? 'gmail-row-read' : 'gmail-row-unread',
          selected ? 'is-active' : '',
          checked ? 'gmail-row-selected' : '',
          isChild ? 'pl-8' : '',
        ].filter(Boolean).join(' ')}
      >
        {!mobile && (
          <div className="pt-0.5 flex-none" style={isChild ? { width: 18 } : undefined}>
            {/* Los mensajes dentro de un hilo desplegado no se seleccionan sueltos: la casilla no hacía
                nada (B5). Se deja el hueco para que no se descuadre la fila. */}
            {!isChild && <RowCheckbox checked={!!checked} onChange={(v) => onCheck?.(v)} />}
          </div>
        )}

        <button
          type="button"
          className={`gmail-star flex-none mt-0.5 ${m.flagged ? 'gmail-star-on' : ''}`}
          onClick={(e) => onAction('star', e)}
          aria-label={m.flagged ? 'Quitar estrella' : 'Destacar'}
          aria-pressed={!!m.flagged}
        >
          <Star className={`h-4 w-4 ${m.flagged ? 'fill-current' : ''}`} strokeWidth={1.75} />
        </button>

        {isThread && !mobile && (
          <span className="flex-none mt-1" style={{ color: 'var(--text-3)' }}>
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        )}

        {!isChild && (
          <SenderAvatar
            email={m.from?.email}
            name={m.from?.name}
            size={mobile ? 36 : 38}
            internalDomains={internalDomains}
            style={{ marginTop: 1 }}
          />
        )}

        {mobile ? (
          /* Móvil: tres líneas compactas (remitente+hora / asunto / vista previa) */
          <div className="gmail-row-mobile-main">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="gmail-row-from-m truncate flex-1"
                style={m.seen ? undefined : { fontWeight: 700 }}
              >
                {fromLabel}
                {isThread && <span className="font-normal opacity-60"> ({m._count})</span>}
              </span>
              {m.visto && <VistoBurbuja visto={m.visto} />}
              <span className="gmail-row-date-m">{fmtDate(m.date)}</span>
              {!m.seen && (
                <span
                  className="flex-none rounded-full"
                  style={{ width: 8, height: 8, background: 'var(--accent)' }}
                  aria-label="No leído"
                />
              )}
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="gmail-row-subject-m truncate"
                style={m.seen ? undefined : { fontWeight: 600 }}
              >
                {m.subject || '(sin asunto)'}
              </span>
              {m.needs_reply && <span className="od-chip od-chip--inv">Responder</span>}
              <AvisoPosibleSpam m={m} />
            </div>
            <div className="gmail-row-snippet-m truncate">
              {m.has_attachments && (
                <Paperclip className="inline h-3 w-3 mr-1 align-text-bottom" style={{ color: 'var(--text-3)' }} />
              )}
              {showAccount && m.accountEmail ? `${m.accountEmail.split('@')[0]} · ` : ''}
              {preview || ' '}
            </div>
          </div>
        ) : (
          <div className="gmail-row-body">
            {/* Línea 1 — remitente · chips · hora */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="gmail-row-from" title={fromLabel}>
                {fromLabel}
                {isThread && <span className="font-normal" style={{ color: 'var(--text-3)' }}> ({m._count})</span>}
              </span>

              {showAccount && m.accountEmail && (
                <span className="od-chip od-chip--proj" title={m.accountEmail}>
                  {m.accountEmail.split('@')[0]}
                </span>
              )}
              {m.needs_reply && <span className="od-chip od-chip--inv">Responder</span>}
              {mostrarArchivado && (m.folder === 'archive' || m._archivado) && <span className="od-chip" data-testid="chip-archivado">Archivado</span>}
              {(m.ai_labels || []).slice(0, 2).map((lb) => (
                <span key={lb} className="od-chip od-chip--work">{lb}</span>
              ))}
              <AvisoPosibleSpam m={m} />

              <span className="ml-auto flex items-center gap-1.5 shrink-0">
                {m.visto && <VistoBurbuja visto={m.visto} />}
                <span className="gmail-row-date">{fmtDate(m.date)}</span>
              </span>
            </div>

            {/* Línea 2 — asunto · vista previa · adjunto · punto de no leído.
                El asunto toma su ancho natural y solo se encoge cuando hace
                falta (antes tenía un tope fijo de 220px del mockup, y se
                cortaba aunque sobrara media pantalla a la derecha). */}
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="gmail-row-subject" style={{ flex: '0 1 auto', minWidth: 0 }}>
                {m.subject || '(sin asunto)'}
              </span>
              {preview && (
                <span className="gmail-row-snippet" style={{ flex: '1 1 0%', minWidth: 0 }}>
                  — {preview}
                </span>
              )}
              {m.has_attachments && (
                <Paperclip className="h-3.5 w-3.5 flex-none ml-auto" style={{ color: 'var(--text-3)' }} />
              )}
              {!m.seen && (
                <span
                  className="flex-none rounded-full"
                  style={{
                    width: 8, height: 8, background: 'var(--accent)',
                    marginLeft: m.has_attachments ? 6 : 'auto',
                  }}
                  aria-label="No leído"
                />
              )}
            </div>
          </div>
        )}

        {!mobile && (
          <div
            className="hidden md:flex absolute right-0 top-0 bottom-0 items-center gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pr-2 pl-10 transition-opacity"
            style={{ background: 'linear-gradient(to right, transparent, var(--panel-2) 30%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={(e) => onAction('archive', e)} aria-label="Archivar">
                  <Archive className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Archivar</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={(e) => onAction('trash', e)} aria-label={definitivo ? 'Eliminar para siempre' : 'Eliminar'}>
                  <Trash2 className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{definitivo ? 'Eliminar para siempre' : 'Eliminar'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={(e) => onAction('spam', e)} aria-label="Spam">
                  <AlertOctagon className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Marcar como spam</TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

/* Estado grande centrado (vacío / sin conexión / sin resultados). */
function EmptyState({ icon: Icon, tone = 'accent', title, children, action }) {
  const box = tone === 'accent'
    ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
    : { background: 'var(--panel)', color: 'var(--text-2)' };
  return (
    <div
      className="h-full flex flex-col items-center justify-center gap-3.5 text-center p-10"
      style={{ color: 'var(--text-3)' }}
    >
      <div className="grid place-items-center" style={{ width: 84, height: 84, borderRadius: 24, ...box }}>
        <Icon className="h-9 w-9" strokeWidth={1.5} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
      <div style={{ fontSize: 14, maxWidth: 290, lineHeight: 1.5 }}>{children}</div>
      {action}
    </div>
  );
}

/** El borrador de /drafts que corresponde a una fila de la carpeta Borradores (asunto + primer destinatario). */
async function borradorDeFila(m) {
  let lista = [];
  try {
    lista = (await api.get('/drafts')).data || [];
  } catch {
    return null;
  }
  const norm = (x) => {
    const t = String(x || '').trim().toLowerCase();
    return t === '(no subject)' || t === '(sin asunto)' ? '' : t;   // marcador del índice = sin asunto
  };
  const primero = (v) => {
    const x = Array.isArray(v) ? v[0] : v;
    return norm(typeof x === 'object' && x ? x.email || x.address : String(x || '').replace(/.*</, '').replace(/>.*/, ''));
  };
  const asunto = norm(m.subject);
  const para = primero(m.to);
  const hallados = lista.filter((d) => {
    if (norm(d.subject) !== asunto) return false;
    const suyo = primero(d.to);
    return !para || !suyo || suyo.includes(para) || para.includes(suyo);
  });
  hallados.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
  const d = hallados[0];
  return d ? { ...d, id: d.id || d._id } : null;
}

export default function MessageList({ mobile }) {
  const qc = useQueryClient();
  const {
    accountFilter, setAccountFilter, activeFolder, setActiveFolder, openMessage, openComposer,
    selectedUid, selectedAccountId, selectedFolder, threaded, toggleThreaded, listSearch, setListSearch,
  } = useStore();
  const [expanded, setExpanded] = useState(() => new Set());
  const [tab, setTab] = useState('primary');
  /* Búsqueda profunda = incluir el CUERPO y los enlaces, que solo tiene el
     proveedor (la caché local guarda encabezados). Cuesta ~1,5 s frente a los
     ~10 ms de la local, así que NO sustituye a la rápida: va detrás. Primero se
     ven los resultados por remitente y asunto, y unos instantes después entran
     los que solo coincidían dentro del texto. */
  const [deepSearch, setDeepSearch] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [borrado, setBorrado] = useState(null);
  const [confirmarMasiva, setConfirmarMasiva] = useState(null);   // { kind, n } (M12)
  const [cancelarProgramado, setCancelarProgramado] = useState(null);   // fila programada (M26)
  /* "Seleccionar todos" solo puede marcar lo que está cargado. Para abarcar la
     carpeta entera hay que traer las páginas que faltan y decirlo mientras pasa;
     dar por buenos 50 cuando hay 800 es lo que hace que una acción masiva
     sorprenda. Tope de seguridad: no se cargan carpetas sin fin. */
  const [cargandoCarpeta, setCargandoCarpeta] = useState(false);
  const [seleccionarAlCargar, setSeleccionarAlCargar] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));

  // Search comes only from topbar / command palette / saved searches (listSearch)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((listSearch || '').trim()), 300);
    return () => clearTimeout(t);
  }, [listSearch]);

  // Cada término nuevo empieza en local (instantáneo) y, sin pedir permiso,
  // pasa solo a buscar también dentro del contenido. Antes hacía falta pulsar
  // un botón, y buscar "vnc" no encontraba el correo que lo llevaba en el
  // cuerpo o en un enlace aunque estuviera ahí.
  useEffect(() => {
    setDeepSearch(false);
    if (!(debouncedSearch || '').trim()) return undefined;
    // El respiro deja pintar los resultados rápidos antes de pedir los lentos.
    const t = setTimeout(() => setDeepSearch(true), 350);
    return () => clearTimeout(t);
  }, [debouncedSearch]);

  /* La barra lateral tiene UNA entrada ("En espera") para programados y
     pospuestos: recordar en cuál estabas hace que vuelva a abrirse ahí,
     venga el cambio de las pestañas, del buscador de comandos o de donde sea. */
  useEffect(() => { recordarTransito(activeFolder); }, [activeFolder]);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const res = await api.get('/accounts');
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.accounts)) return res.data.accounts;
      if (Array.isArray(res.data?.items)) return res.data.items;
      return [];
    },
  });

  /* Dominios propios: un remitente de estos es un compañero, y su avatar
     puede ser su foto de perfil en vez del logo de una empresa. */
  const internalDomains = useMemo(() => {
    const set = new Set();
    for (const a of accounts) {
      const at = (a?.email || '').lastIndexOf('@');
      if (at > -1) set.add(a.email.slice(at + 1).toLowerCase());
    }
    return Array.from(set);
  }, [accounts]);

  const isSnoozedFolder = activeFolder === 'snoozed';
  const isStarredFolder = activeFolder === 'starred';
  const isScheduledFolder = activeFolder === 'scheduled';
  const isNeedsReplyFolder = activeFolder === 'needs_reply';
  // Server IMAP search when query is meaningful (≥2 chars) and not virtual folders
  const useServerSearch = debouncedSearch.length >= 2 && !isSnoozedFolder && !isScheduledFolder;
  /* Categoría filtrada por el BACKEND (campo `category`/`needs_reply` de
     mail_index) para que "cargar más" pagine dentro de la pestaña: la bandeja
     sin búsqueda activa y «Por responder» (lib/carpetaCorreo.js). */
  const { carpeta: fetchFolder, categoria: serverCategory } = peticionDeCarpeta(activeFolder, tab, useServerSearch);

  // Infinite query: solo la bandeja unificada pagina de verdad (offset contra la
  // caché del backend, `has_more`); scheduled/snoozed/búsqueda devuelven 1 página.
  const {
    data, isFetching, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: isScheduledFolder
      ? ['scheduled']
      : isSnoozedFolder
        ? ['snoozed']
        : useServerSearch
          ? ['search-unified', debouncedSearch, fetchFolder, deepSearch]
          : ['unified', fetchFolder, serverCategory],
    queryFn: async ({ pageParam = 0 }) => {
      if (isScheduledFolder) {
        const rows = (await api.get('/scheduled')).data || [];
        const messages = (Array.isArray(rows) ? rows : []).map((s) => ({
          uid: s.id,
          accountId: s.account_id,
          folder: 'scheduled',
          subject: s.subject || '(sin asunto)',
          from: { email: (s.to || [])[0] || '', name: (s.to || []).join(', ') },
          to: s.to || [],
          date: s.send_at,
          seen: true,
          flagged: false,
          scheduledId: s.id,
          scheduleStatus: s.status,
          scheduleAttempts: s.attempts || 0,
          scheduleMax: s.max_attempts || 5,
          scheduleError: s.last_error || s.error,
          preview: s.status === 'scheduled'
            ? `Sale ${s.send_at ? textoProgramado(new Date(s.send_at)) : '—'}`
            : s.status === 'sent'
              ? 'Enviado'
              : s.status === 'failed'
                ? `Fallido: ${s.last_error || s.error || ''}`
                : s.status === 'cancelled'
                  ? 'Cancelado'
                  : s.status,
        }));
        return { messages };
      }
      if (isSnoozedFolder) {
        const rows = (await api.get('/mail/snoozed')).data || [];
        const messages = (Array.isArray(rows) ? rows : []).map((s) => ({
          uid: s.uid,
          accountId: s.account_id,
          folder: s.park_folder || 'archive',
          subject: s.subject || '(sin asunto)',
          from: { email: s.from_email, name: s.from_name },
          date: s.until,
          seen: true,
          flagged: false,
          message_id: s.message_id,
          snoozeId: s.id,
          snoozeUntil: s.until,
          preview: s.until ? `Vuelve ${textoProgramado(new Date(s.until))}` : 'Pospuesto',
        }));
        return { messages };
      }
      if (useServerSearch) {
        const folderParam = fetchFolder && !['starred', 'inbox'].includes(fetchFolder) ? fetchFolder : 'all';
        // For inbox / starred prefer broad search; raw IMAP folders search that folder
        const folder = (fetchFolder === 'inbox' || isStarredFolder || !fetchFolder) ? 'all' : folderParam;
        const scopeParam = deepSearch ? '&scope=full' : '';
        return (await api.get(`/mail/search/unified?q=${encodeURIComponent(debouncedSearch)}&folder=${encodeURIComponent(folder)}&limit=60${scopeParam}`)).data;
      }
      // starred uses IMAP FLAGGED multi-account search on the backend
      const catParam = serverCategory ? `&category=${encodeURIComponent(serverCategory)}` : '';
      return (await api.get(`/mail/unified?folder=${encodeURIComponent(fetchFolder)}&limit=${PAGINA}&offset=${pageParam}${catParam}`)).data;
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      if (!last?.has_more) return undefined;
      return pages.reduce((n, p) => n + (p?.messages?.length || 0), 0);
    },
    enabled: accounts.length > 0 || isScheduledFolder,
    refetchInterval: isScheduledFolder ? 20000 : 60000,
    refetchOnWindowFocus: true,
  });

  /* Contadores de las pestañas. Comparten clave con los de la barra lateral,
     así que no son una petición extra: los dos leen la misma caché. */
  const enTransito = isScheduledFolder || isSnoozedFolder;
  const { data: scheduledCount = 0 } = useQuery({
    queryKey: ['scheduled-count'],
    queryFn: async () => (await api.get('/scheduled')).data,
    enabled: enTransito,
    select: (r) => (Array.isArray(r) ? r : []).filter((x) => ['scheduled', 'retry', 'sending'].includes(x.status)).length,
  });
  const { data: snoozedCount = 0 } = useQuery({
    queryKey: ['snoozed-count'],
    queryFn: async () => (await api.get('/mail/snoozed')).data,
    enabled: enTransito,
    select: (r) => (Array.isArray(r) ? r : []).length,
  });

  const allMessages = useMemo(() => {
    const flat = (data?.pages || []).flatMap((p) => p?.messages || []);
    // Dedupe entre páginas (el offset puede correrse si entra correo nuevo).
    const seen = new Set();
    const out = [];
    for (const m of flat) {
      const k = `${m.accountId}-${m.folder || ''}-${m.uid}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(m);
    }
    return out;
  }, [data]);

  const filtered = useMemo(() => {
    let list = allMessages;
    if (accountFilter !== 'all') list = list.filter((m) => m.accountId === accountFilter);
    // Sin búsqueda, el servidor ya devolvió solo «Por responder» (serverCategory)
    if (isNeedsReplyFolder && !serverCategory) {
      list = list.filter((m) => m.needs_reply || (m.ai_labels || []).some((l) => /respond|responder|urgente/i.test(l)));
    }
    // Client-side refine when not using server search (short queries)
    if (!useServerSearch) {
      const q = (listSearch || '').trim().toLowerCase();
      if (q) {
        list = list.filter((m) =>
          (m.subject || '').toLowerCase().includes(q) ||
          (m.from?.name || '').toLowerCase().includes(q) ||
          (m.from?.email || '').toLowerCase().includes(q) ||
          (m.accountName || '').toLowerCase().includes(q) ||
          (m.preview || '').toLowerCase().includes(q) ||
          (m.ai_labels || []).some((l) => String(l).toLowerCase().includes(q)));
      }
    }
    return list;
  }, [allMessages, accountFilter, listSearch, useServerSearch, isNeedsReplyFolder, serverCategory]);

  const categorized = useMemo(() => {
    if (activeFolder !== 'inbox') return filtered;
    // Si el servidor ya filtró por categoría, no volvemos a filtrar aquí: la
    // heurística de cliente descartaría correos legítimos de la página.
    if (serverCategory) return filtered;
    if (tab === 'needs_reply') {
      return filtered.filter((m) => m.needs_reply || (m.ai_labels || []).some((l) => /respond|responder|urgente/i.test(l)));
    }
    return filterByCategory(filtered, tab);
  }, [filtered, tab, activeFolder, serverCategory]);

  const showAccount = accountFilter === 'all' && accounts.length > 1;
  const toggleExpand = (key) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const rows = useMemo(() => {
    let base = [];
    if (threaded) {
      const map = new Map();
      for (const m of categorized) {
        const k = threadKey(m);
        if (!map.has(k)) map.set(k, { rep: { ...m }, items: [m], count: 1, anyUnseen: !m.seen });
        else {
          const e = map.get(k);
          e.items.push(m);
          e.count += 1;
          if (!m.seen) e.anyUnseen = true;
        }
      }
      for (const [k, e] of map.entries()) {
        base.push({
          ...e.rep, _count: e.count, _threadKey: k, _isThread: e.count > 1, seen: !e.anyUnseen,
          // Todos los correos de la conversación: archivar/eliminar la fila actúa sobre todos (M11)
          _miembros: e.items.map((i) => ({ accountId: i.accountId, folder: i.folder, uid: i.uid, message_id: i.message_id })),
        });
        if (e.count > 1 && expanded.has(k)) {
          const children = e.items.slice().sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          for (const c of children) base.push({ ...c, _child: true, _threadKey: k });
        }
      }
    } else {
      base = categorized.map((m) => ({ ...m }));
    }
    let lastGroup = null;
    return base.map((m) => {
      if (m._child) return m;
      const g = dateGroup(m.date);
      const showGroup = g !== lastGroup;
      lastGroup = g;
      return { ...m, _group: showGroup ? g : null };
    });
  }, [categorized, threaded, expanded]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['unified'] });
    qc.invalidateQueries({ queryKey: ['unified-badge'] });
  };

  /* Quita de la lista en el acto lo que se acaba de mover (B8): el refresco del servidor tarda (vuelve a pedir
     todas las páginas cargadas) y hasta entonces el correo seguía ahí. Si la acción fallara, ese refresco
     lo devuelve. `correos`: [{accountId, folder, uid}]. */
  const quitarDeLista = (correos, destino = null) => {
    // En «Todos» y «Destacados» lo archivado sigue a la vista: ahí no se quita, se marca (etiqueta «Archivado»)
    if (destino === 'archive' && (activeFolder === 'all' || activeFolder === 'starred')) {
      qc.setQueriesData({ queryKey: ['unified'] }, (old) => marcarArchivadas(old, correos));
      qc.setQueriesData({ queryKey: ['search-unified'] }, (old) => marcarArchivadas(old, correos));
      return;
    }
    const fuera = (m) => correos.some((c) => c.accountId === m.accountId && String(c.uid) === String(m.uid)
      && (!m.folder || !c.folder || m.folder === c.folder));
    const limpiar = (old) => (old?.pages
      ? { ...old, pages: old.pages.map((p) => (p?.messages ? { ...p, messages: p.messages.filter((m) => !fuera(m)) } : p)) }
      : old);
    qc.setQueriesData({ queryKey: ['unified'] }, limpiar);
    qc.setQueriesData({ queryKey: ['search-unified'] }, limpiar);
  };

  const undo = async (m, fromFolder, toFolder) => {
    try {
      await api.post('/mail/undo-move', {
        account_id: m.accountId, from_folder: fromFolder, to_folder: toFolder, message_id: m.message_id,
      });
      invalidate();
      toast.success('Restaurado');
    } catch {
      toast.error('No se pudo deshacer');
    }
  };

  /* Conversación agrupada (M11): antes archivar/eliminar movía solo el mensaje que
     representa a la fila y avisaba «Conversación archivada» con el resto aún en la bandeja. */
  const accionSobreConversacion = async (m, type) => {
    const destino = { archive: 'archive', trash: 'trash', spam: 'spam' }[type];
    /* Solo los correos que tiene sentido mover (B1, auditoría 2026-10-05): en «Todos», «Destacados»
       o una búsqueda el hilo incluye TUS respuestas (Enviados), que no deben acabar en Archivo o
       Spam; y los que ya están en el destino no se mueven otra vez. Dentro de esa misma carpeta
       (p. ej. eliminar desde la Papelera) sí cuentan los que están en ella. */
    const todos = m._miembros.map((x) => ({ ...x, folder: x.folder || activeFolder }));
    let miembros = todos.filter((x) => x.folder !== 'sent' && (x.folder !== destino || activeFolder === destino));
    if (!miembros.length) miembros = [{ accountId: m.accountId, folder: m.folder || activeFolder, uid: m.uid, message_id: m.message_id }];
    const grupos = new Map();
    for (const x of miembros) {
      const gk = `${x.accountId}|${x.folder}`;
      if (!grupos.has(gk)) grupos.set(gk, { accountId: x.accountId, folder: x.folder, uids: [] });
      grupos.get(gk).uids.push(x.uid);
    }
    const lista = [...grupos.values()];
    if (type === 'trash' && lista.some((g) => g.folder === 'trash')) {
      setBorrado({ cantidad: miembros.length, ejecutar: () => eliminarGrupos(lista) });
      return;
    }
    for (const g of lista) {
      if (type === 'trash') await api.post('/mail/delete', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: 'trash' });
      else await api.post('/mail/move', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: destino });
    }
    quitarDeLista(miembros, destino);
    const n = miembros.length;
    const texto = {
      archive: activeFolder === 'starred' || activeFolder === 'all'
        ? `Conversación archivada (${n} correos) · ${avisoArchivado(activeFolder, !!m.flagged).split(' · ')[1]}`
        : `Conversación archivada (${n} correos) · la encuentras en «Todos»`,
      trash: `Conversación enviada a la papelera (${n} correos)`,
      spam: `Conversación marcada como spam (${n} correos)`,
    }[type];
    const paraDeshacer = n <= MAX_DESHACER_MASIVO
      ? miembros.filter((x) => x.message_id).map((x) => ({ accountId: x.accountId, desde: destino, hacia: x.folder, messageId: x.message_id }))
      : [];
    if (paraDeshacer.length) toast.success(texto, { duration: 7000, action: { label: 'Deshacer', onClick: () => deshacerVarios(paraDeshacer, n) } });
    else toast.success(texto);
    invalidate();
  };

  const action = async (m, type, e) => {
    if (e) e.stopPropagation();
    const accId = m.accountId;
    const fld = m.folder || activeFolder;
    try {
      // Solo hilos de verdad (`t:` = thread_id de References/In-Reply-To). Agrupados por asunto
      // (sin thread_id) juntaban correos sin relación: ahí se actúa solo sobre la fila (B2).
      if (m._isThread && !m._child && m._miembros?.length > 1 && String(m._threadKey || '').startsWith('t:')
          && ['archive', 'trash', 'spam'].includes(type)) {
        await accionSobreConversacion(m, type);
        return;
      }
      if (type === 'star') {
        await api.post('/mail/flag', {
          account_id: accId, folder: fld, uids: [m.uid], flag: 'flagged', value: !m.flagged,
        });
      } else if (type === 'archive') {
        await api.post('/mail/move', { account_id: accId, folder: fld, uids: [m.uid], target: 'archive' });
        quitarDeLista([{ accountId: accId, folder: fld, uid: m.uid }], 'archive');
        if (m.message_id) {
          toast.success(avisoArchivado(activeFolder, !!m.flagged), {
            duration: 6000,
            action: { label: 'Deshacer', onClick: () => undo(m, 'archive', fld) },
          });
        } else toast.success(avisoArchivado(activeFolder, !!m.flagged));
      } else if (type === 'trash') {
        // En la Papelera es definitivo: pasa por la confirmación (también al deslizar).
        if (fld === 'trash') {
          setBorrado({
            cantidad: 1,
            ejecutar: () => eliminarGrupos([{ accountId: accId, folder: fld, uids: [m.uid] }]),
          });
          return;
        }
        await api.post('/mail/delete', { account_id: accId, folder: fld, uids: [m.uid], target: 'trash' });
        quitarDeLista([{ accountId: accId, folder: fld, uid: m.uid }]);
        if (m.message_id) {
          toast.success('Enviado a la papelera', {
            duration: 6000,
            action: { label: 'Deshacer', onClick: () => undo(m, 'trash', fld) },
          });
        } else toast.success('Movido a papelera');
      } else if (type === 'spam') {
        await api.post('/mail/move', { account_id: accId, folder: fld, uids: [m.uid], target: 'spam' });
        quitarDeLista([{ accountId: accId, folder: fld, uid: m.uid }]);
        if (m.message_id) {
          toast.success('Marcado como spam', {
            duration: 6000,
            action: { label: 'Deshacer', onClick: () => undo(m, 'spam', fld) },   // antes no tenía (M12)
          });
        } else toast.success('Marcado como spam');
      }
      invalidate();
    } catch (err) {
      const que = { star: 'cambiar la estrella', archive: 'archivar', trash: 'mover a la papelera', spam: 'marcar como spam' }[type];
      toast.error(mensajeErrorRed(err, que));
      invalidate();   // lo que sí se movió antes del fallo (una conversación son varias llamadas) tiene que verse (B6)
    }
  };

  const wakeSnooze = async (m, { open = false } = {}) => {
    if (!m.snoozeId) return;
    try {
      const { data: r } = await api.post(`/mail/snooze/${m.snoozeId}/wake`);
      toast.success('Despertado · en Recibidos');
      qc.invalidateQueries({ queryKey: ['snoozed'] });
      qc.invalidateQueries({ queryKey: ['snoozed-count'] });
      qc.invalidateQueries({ queryKey: ['unified'] });
      qc.invalidateQueries({ queryKey: ['unified-badge'] });
      // Al volver a Recibidos el correo tiene OTRO uid (QA 2026-10-06: se abría el viejo y daba 502)
      if (open && r?.uid) openMessage(m.accountId, 'inbox', r.uid);
    } catch {
      toast.error('No se pudo despertar el mensaje');
    }
  };

  const rescheduleSnooze = async (m, hours) => {
    if (!m.snoozeId) return;
    const at = new Date(Date.now() + hours * 3600 * 1000);
    try {
      await api.post(`/mail/snooze/${m.snoozeId}/reschedule`, { until: at.toISOString() });
      toast.success(`Pospuesto de nuevo hasta ${textoProgramado(at)}`);
      qc.invalidateQueries({ queryKey: ['snoozed'] });
      qc.invalidateQueries({ queryKey: ['snoozed-count'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo posponer de nuevo');
    }
  };

  // El orden que se ve, para «Después de archivar: abrir el siguiente» en el lector (M32)
  const setOrdenLista = useStore((s) => s.setOrdenLista);
  useEffect(() => {
    setOrdenLista(rows.filter((r) => !r.snoozeId && !r.scheduledId)
      .map((r) => ({ accountId: r.accountId, folder: r.folder || activeFolder, uid: String(r.uid) })));
  }, [rows, activeFolder, setOrdenLista]);

  /* j / k: abrir el correo siguiente / anterior de la lista (B10). Sobre las filas visibles; una conversación
     plegada cuenta como su correo más reciente. */
  useEffect(() => {
    const tecla = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || (e.key !== 'j' && e.key !== 'k')) return;
      const el = document.activeElement;
      if (/input|textarea|select/i.test(el?.tagName) || el?.isContentEditable) return;
      if (document.querySelector('[role="dialog"][data-state="open"], [data-testid="composer"]')) return;
      const lista = rows.filter((r) => !r.snoozeId && !r.scheduledId);
      if (!lista.length) return;
      const i = lista.findIndex((r) => String(r.uid) === String(selectedUid));
      const sig = e.key === 'j' ? (i < 0 ? 0 : Math.min(lista.length - 1, i + 1)) : Math.max(0, i - 1);
      const m = lista[sig];
      if (!m || (i === sig && i >= 0)) return;
      e.preventDefault();
      openMessage(m.accountId, m.folder || activeFolder, m.uid);
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  });

  const onRowClick = async (m) => {
    if (isSnoozedFolder && m.snoozeId) {
      // Default click: wake and open
      await wakeSnooze(m, { open: true });
      return;
    }
    // Borradores: desde 2026-10-03 el servidor copia cada borrador a esta carpeta del buzón.
    // Pulsarlo sigue escribiendo ESE borrador (su id de /drafts), no abre el lector.
    if ((m.folder || activeFolder) === 'drafts') {
      const d = await borradorDeFila(m);
      if (d) { openComposer('new', d); return; }
    }
    if (threaded && m._isThread) toggleExpand(m._threadKey);
    else openMessage(m.accountId, m.folder || activeFolder, m.uid);
  };

  const cancelScheduled = async (m) => {
    if (!m.scheduledId) return;
    try {
      await api.delete(`/scheduled/${m.scheduledId}`);
      toast.success('Envío cancelado');
      qc.invalidateQueries({ queryKey: ['scheduled'] });
      qc.invalidateQueries({ queryKey: ['scheduled-count'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const sendScheduledNow = async (m) => {
    if (!m.scheduledId) return;
    try {
      await api.post(`/scheduled/${m.scheduledId}/send-now`);
      toast.success('Enviado ahora');
      qc.invalidateQueries({ queryKey: ['scheduled'] });
      qc.invalidateQueries({ queryKey: ['scheduled-count'] });
      qc.invalidateQueries({ queryKey: ['unified'] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
  };

  const saveSearch = async () => {
    const q = (listSearch || '').trim();
    if (!q) return;
    await api.post('/saved-searches', { name: q, query: q, field: 'text' });
    qc.invalidateQueries({ queryKey: ['saved-searches'] });
    toast.success('Búsqueda guardada');
  };

  const toggleCheck = (key, v) => {
    setChecked((prev) => {
      const n = new Set(prev);
      if (v) n.add(key); else n.delete(key);
      return n;
    });
  };

  /* ---------- selección múltiple ---------- */
  const selectableRows = useMemo(() => rows.filter((r) => !r._child), [rows]);
  /* La clave lleva la carpeta (B5): en «Todos» o una búsqueda el mismo uid puede existir en Recibidos y
     en Enviados de la misma cuenta, y la acción caía sobre el otro correo. */
  const claveFila = (r) => `${r.accountId}|${r.folder || activeFolder}|${r.uid}`;
  const rowByKey = useMemo(() => {
    const map = new Map();
    for (const r of selectableRows) map.set(claveFila(r), r);
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableRows, activeFolder]);

  /* Qué correos toca una acción masiva sobre una fila (B3): si es una conversación de verdad, todos sus
     correos, con las mismas reglas que la acción de una fila (B1/B2): sin Enviados ni los que ya están
     en el destino. Marcar leído/no leído sí abarca el hilo entero. */
  const correosDeFila = (m, kind) => {
    const fila = { accountId: m.accountId, folder: m.folder || activeFolder, uid: m.uid, message_id: m.message_id };
    if (!(m._isThread && m._miembros?.length > 1 && String(m._threadKey || '').startsWith('t:'))) return [fila];
    const todos = m._miembros.map((x) => ({ ...x, folder: x.folder || activeFolder }));
    if (kind === 'read' || kind === 'unread') return todos;
    const destino = { archive: 'archive', trash: 'trash', spam: 'spam' }[kind];
    const elegidos = todos.filter((x) => x.folder !== 'sent' && (x.folder !== destino || activeFolder === destino));
    return elegidos.length ? elegidos : [fila];
  };

  const selCount = checked.size;
  const allChecked = selectableRows.length > 0 && selCount === selectableRows.length;

  const toggleSelectAll = (v) => {
    if (v) setChecked(new Set(selectableRows.map(claveFila)));
    else setChecked(new Set());
  };

  const clearSelection = () => {
    setChecked(new Set());
    setSeleccionarAlCargar(false);
  };

  const TOPE_CARPETA = 2000;

  /** Trae las páginas que faltan y, al terminar, marca toda la carpeta. */
  const seleccionarCarpetaEntera = async () => {
    setCargandoCarpeta(true);
    setSeleccionarAlCargar(true);
    try {
      for (let i = 0; i < Math.ceil(TOPE_CARPETA / PAGINA); i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetchNextPage();
        const cargados = (r?.data?.pages || []).reduce((n, p) => n + (p?.messages?.length || 0), 0);
        if (!r?.hasNextPage || cargados >= TOPE_CARPETA) break;
      }
    } catch {
      toast.error('No se pudo cargar toda la carpeta');
      setSeleccionarAlCargar(false);
    } finally {
      setCargandoCarpeta(false);
    }
  };

  // Al terminar de cargar se marca todo lo que hay (las filas ya están en `rows`)
  useEffect(() => {
    if (!seleccionarAlCargar || cargandoCarpeta) return;
    setChecked(new Set(selectableRows.map(claveFila)));
    setSeleccionarAlCargar(false);
    // claveFila solo depende de activeFolder, que ya cambia selectableRows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionarAlCargar, cargandoCarpeta, selectableRows]);

  /* `/mail/delete` decide por carpeta: desde la Papelera expurga, desde
     cualquier otra mueve a la Papelera. El aviso cuenta cada cosa por separado. */
  const eliminarGrupos = async (grupos) => {
    const suma = (lista) => lista.reduce((t, g) => t + g.uids.length, 0);
    const definitivos = suma(grupos.filter((g) => g.folder === 'trash'));
    const aPapelera = suma(grupos.filter((g) => g.folder !== 'trash'));
    try {
      for (const g of grupos) {
        await api.post('/mail/delete', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: 'trash' });
      }
      toast.success(avisoEliminados(definitivos, aPapelera));
      clearSelection();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo eliminar');
    }
    invalidate();
  };

  /* Agrupa la selección por cuenta+carpeta y lanza una llamada por grupo. */
  /* Deshacer una acción masiva: /mail/undo-move va de uno en uno (busca cada correo por su
     Message-ID), así que solo se ofrece para pocos correos — 2000 búsquedas seguidas golpearían
     al proveedor (regla 8). Por encima de ese número, la acción pide confirmación antes (M12). */
  const MAX_DESHACER_MASIVO = 20;
  /* Con progreso y cuentas honestas (B7): eran hasta 20 llamadas sin nada en pantalla, y los correos sin
     Message-ID (que no se pueden buscar para devolverlos) se omitían sin decirlo. */
  const deshacerVarios = async (items, total = items.length) => {
    let ok = 0;
    const aviso = toast.loading(`Restaurando 0 de ${items.length}…`);
    for (const it of items) {
      try {
        await api.post('/mail/undo-move', {
          account_id: it.accountId, from_folder: it.desde, to_folder: it.hacia, message_id: it.messageId,
        });
        ok += 1;
      } catch { /* se cuenta abajo */ }
      toast.loading(`Restaurando ${ok} de ${items.length}…`, { id: aviso });
    }
    invalidate();
    const sinId = total - items.length;
    const extra = sinId > 0 ? ` ${sinId} no se pueden devolver desde aquí: búscalos en su carpeta.` : '';
    if (ok === items.length && !sinId) toast.success(ok === 1 ? 'Restaurado' : `${ok} restaurados`, { id: aviso });
    else toast.error(`Se restauraron ${ok} de ${total}.${extra}`, { id: aviso, duration: 9000 });
  };

  const bulkAction = async (kind, confirmado = false) => {
    const groups = new Map();
    const correos = [];
    for (const key of checked) {
      const m = rowByKey.get(key);
      if (!m) continue;
      for (const c of correosDeFila(m, kind)) {
        const gk = `${c.accountId}|${c.folder}`;
        if (!groups.has(gk)) groups.set(gk, { accountId: c.accountId, folder: c.folder, uids: [] });
        if (!groups.get(gk).uids.includes(c.uid)) { groups.get(gk).uids.push(c.uid); correos.push(c); }
      }
    }
    if (!groups.size) { toast.message('No hay nada seleccionado que se pueda mover.'); return; }
    if (kind === 'trash') {
      const grupos = [...groups.values()];
      const definitivos = grupos.filter((g) => g.folder === 'trash').reduce((t, g) => t + g.uids.length, 0);
      if (definitivos > 0) {
        setBorrado({
          cantidad: definitivos,
          aPapelera: grupos.reduce((t, g) => t + g.uids.length, 0) - definitivos,
          ejecutar: () => eliminarGrupos(grupos),
        });
        return;
      }
    }
    const n = correos.length;   // correos de verdad, no filas: una conversación puede ser varios (B3)
    const mueve = kind === 'archive' || kind === 'trash' || kind === 'spam';
    if (mueve && n > MAX_DESHACER_MASIVO && !confirmado) {
      setConfirmarMasiva({ kind, n });
      return;
    }
    const destino = { archive: 'archive', trash: 'trash', spam: 'spam' }[kind];
    const paraDeshacer = mueve && n <= MAX_DESHACER_MASIVO
      ? correos.filter((c) => c.message_id).map((c) => ({
        accountId: c.accountId, desde: destino, hacia: c.folder, messageId: c.message_id,
      }))
      : [];
    try {
      for (const g of groups.values()) {
        if (kind === 'archive') {
          await api.post('/mail/move', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: 'archive' });
        } else if (kind === 'trash') {
          await api.post('/mail/delete', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: 'trash' });
        } else if (kind === 'spam') {
          await api.post('/mail/move', { account_id: g.accountId, folder: g.folder, uids: g.uids, target: 'spam' });
        } else if (kind === 'read' || kind === 'unread') {
          await api.post('/mail/flag', {
            account_id: g.accountId, folder: g.folder, uids: g.uids, flag: 'seen', value: kind === 'read',
          });
        }
      }
      if (mueve) quitarDeLista(correos, destino);
      const msg = {
        archive: activeFolder === 'starred' || activeFolder === 'all'
          ? avisoArchivado(activeFolder, activeFolder === 'all' || correos.every((c) => c.flagged !== false), n)
          : `${n} archivado${n === 1 ? '' : 's'}`,
        trash: `${n} movido${n === 1 ? '' : 's'} a la papelera`,
        spam: `${n} marcado${n === 1 ? '' : 's'} como spam`,
        read: `${n} marcado${n === 1 ? '' : 's'} como leído`,
        unread: `${n} marcado${n === 1 ? '' : 's'} como no leído`,
      }[kind];
      if (paraDeshacer.length) {
        toast.success(msg, { duration: 7000, action: { label: 'Deshacer', onClick: () => deshacerVarios(paraDeshacer, n) } });
      } else toast.success(msg);
      clearSelection();
      invalidate();
    } catch (err) {
      toast.error(mensajeErrorRed(err, 'completar la acción'));
      invalidate();   // lo que sí se movió antes del fallo tiene que verse
    }
  };

  const folderTitle = FOLDER_LABELS[activeFolder] || activeFolder;
  // Desde Recibidos o Destacados la búsqueda es en TODO el correo (archivado y enviado incluidos): la cabecera
  // decía «Recibidos» sobre resultados de Archivo (QA 2026-10-06).
  const tituloLista = useServerSearch && (activeFolder === 'inbox' || isStarredFolder) ? 'Búsqueda en todo el correo' : folderTitle;
  const searching = (listSearch || '').trim().length > 0;
  // El servidor cuenta la carpeta entera (lo cargado es una parte), pero solo si lo que se ve es su respuesta
  const { total: totalCount, texto: folderMeta } = contadorDeCarpeta({
    primeraPagina: data?.pages?.[0],
    visibles: activeFolder === 'inbox' ? categorized : filtered,
    delServidor: !useServerSearch && !searching && accountFilter === 'all',
    pestana: activeFolder === 'inbox' && !useServerSearch && !searching
      ? { id: tab, etiqueta: (TABS.find((t) => t.id === tab) || {}).label || 'esta pestaña' } : null,
  });
  // No leídos de cada pestaña de Recibidos (bug 3), de la misma respuesta que la lista
  // Con filtro de cuenta, no: la lista pide todas las cuentas y filtra aquí; el conteo sería de todas
  const conteoPestanas = activeFolder === 'inbox' && accountFilter === 'all' ? data?.pages?.[0]?.pestanas : null;
  const pestanaActual = activeFolder === 'inbox' && !searching ? TABS.find((t) => t.id === tab) : null;
  const vacioPestana = pestanaActual ? vacioDePestana(tab, pestanaActual.label, conteoPestanas) : null;
  const showSkeleton = isFetching && !allMessages.length && !isError;

  return (
    <TooltipProvider delayDuration={200}>
      <div className={`h-full flex flex-col gmail-list ${mobile ? 'gmail-list--mobile' : ''}`}>
        {/* ---------- Toolbar ---------- */}
        {selCount > 0 && !mobile ? (
          <div
            className="gmail-list-toolbar"
            style={{ background: 'var(--accent-soft)', gap: 2, animation: 'od-fade .15s ease' }}
            data-testid="selection-toolbar"
          >
            <button type="button" className="od-tool-btn" onClick={clearSelection} title="Cancelar" aria-label="Cancelar selección">
              <XIcon className="h-[18px] w-[18px]" />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-2)', margin: '0 10px 0 4px' }}>
              {selCount} seleccionado{selCount === 1 ? '' : 's'}
            </span>
            {/* Lo que hay cargado no es toda la carpeta: si falta, se dice y se ofrece traerla */}
            {hasNextPage && totalCount > selCount && (
              <button
                type="button"
                onClick={seleccionarCarpetaEntera}
                disabled={cargandoCarpeta}
                data-testid="select-whole-folder"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--accent-2)',
                  textDecoration: 'underline', opacity: cargandoCarpeta ? .6 : 1,
                }}
              >
                {cargandoCarpeta
                  ? `Cargando… ${allMessages.length} de ${totalCount}`
                  : `Seleccionar ${totalCount > TOPE_CARPETA ? `los primeros ${TOPE_CARPETA}` : `los ${totalCount}`} de ${folderTitle}`}
              </button>
            )}
            <div className="flex-1" />
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => bulkAction('archive')} aria-label="Archivar" data-testid="bulk-archive">
                  <Archive className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Archivar</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => bulkAction('trash')} aria-label={activeFolder === 'trash' ? 'Eliminar para siempre' : 'Eliminar'} data-testid="bulk-trash">
                  <Trash2 className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{activeFolder === 'trash' ? 'Eliminar para siempre' : 'Eliminar'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => bulkAction('read')} aria-label="Marcar como leído" data-testid="bulk-read">
                  <MailOpen className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Marcar como leído</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => bulkAction('unread')} aria-label="Marcar como no leído" data-testid="bulk-unread">
                  <Mail className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Marcar como no leído</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="od-tool-btn" onClick={() => bulkAction('spam')} aria-label="Marcar como spam" data-testid="bulk-spam">
                  <AlertOctagon className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Spam</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className={`gmail-list-toolbar ${mobile ? 'gmail-list-toolbar--mobile' : ''}`}>
            {mobile ? (
              <>
                <div className="min-w-0 flex-1 pl-1">
                  <div
                    className="truncate"
                    style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.15 }}
                    data-testid="mobile-folder-title"
                  >
                    {tituloLista}
                  </div>
                  <div className="truncate" style={{ fontSize: 11, color: 'var(--text-3)' }}>{folderMeta}</div>
                </div>
                <button type="button" className="od-tool-btn" onClick={() => refetch()} data-testid="refresh-button" aria-label="Actualizar">
                  <RefreshCw className={`h-[18px] w-[18px] ${isFetching ? 'od-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  className="od-tool-btn"
                  style={threaded ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}
                  onClick={() => { setExpanded(new Set()); toggleThreaded(); }}
                  data-testid="threaded-toggle"
                  aria-label="Conversaciones"
                >
                  <Layers className="h-[18px] w-[18px]" />
                </button>
              </>
            ) : (
              <>
                <RowCheckbox
                  checked={allChecked}
                  onChange={toggleSelectAll}
                  label="Seleccionar todos"
                  className="ml-1"
                  testId="select-all"
                />
                <div className="flex flex-col min-w-0 ml-0.5">
                  <span
                    className="truncate"
                    style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.1 }}
                    data-testid="folder-title"
                  >
                    {tituloLista}
                  </span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--text-3)' }}>{folderMeta}</span>
                </div>

                <div className="flex-1" />

                {accounts.length > 1 && (
                  <Select value={accountFilter} onValueChange={setAccountFilter}>
                    <SelectTrigger
                      data-testid="account-filter"
                      className="h-8 w-auto max-w-[180px] border-0 shadow-none text-xs bg-transparent focus:ring-0"
                      style={{ color: 'var(--text-3)' }}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="account-filter-all">Todas las cuentas</SelectItem>
                      {accounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="od-tool-btn" onClick={() => refetch()} data-testid="refresh-button" aria-label="Actualizar">
                      <RefreshCw className={`h-[18px] w-[18px] ${isFetching ? 'od-spin' : ''}`} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Actualizar</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="od-tool-btn"
                      style={threaded ? { background: 'var(--accent-soft)', color: 'var(--accent)' } : undefined}
                      onClick={() => { setExpanded(new Set()); toggleThreaded(); }}
                      data-testid="threaded-toggle"
                      aria-label="Conversaciones"
                    >
                      <Layers className="h-[18px] w-[18px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{threaded ? 'Vista de conversaciones' : 'Vista clásica'}</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        )}

        {/* ---------- Pestañas de categorías ---------- */}
        {activeFolder === 'inbox' && (
          <div className={`gmail-tabs ${mobile ? 'gmail-tabs--mobile' : ''}`}>
            {TABS.map((tb) => {
              const Icon = tb.icon;
              const active = tab === tb.id;
              return (
                <button
                  key={tb.id}
                  type="button"
                  className={`gmail-tab ${active ? 'gmail-tab-active' : ''}`}
                  onClick={() => setTab(tb.id)}
                  data-testid={`category-tab-${tb.id}`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{mobile ? tb.short : tb.label}</span>
                  <ConteoPestana conteo={conteoPestanas} id={tb.id} />
                </button>
              );
            })}
          </div>
        )}

        {/* ---------- Programados / Pospuestos: lo que está en tránsito ---------- */}
        {(isScheduledFolder || isSnoozedFolder) && (
          <div className={`gmail-tabs ${mobile ? 'gmail-tabs--mobile' : ''}`} data-testid="transito-tabs">
            {TRANSITO.map((tb) => {
              const Icon = tb.icon;
              const active = activeFolder === tb.id;
              const n = tb.id === 'scheduled' ? scheduledCount : snoozedCount;
              return (
                <button
                  key={tb.id}
                  type="button"
                  className={`gmail-tab ${active ? 'gmail-tab-active' : ''}`}
                  onClick={() => setActiveFolder(tb.id)}
                  data-testid={`transito-tab-${tb.id}`}
                >
                  <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{tb.label}</span>
                  {n > 0 && <span className="od-chip od-chip--inv">{n}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* ---------- Chip de búsqueda activa ---------- */}
        {!isScheduledFolder && searching && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 flex-none"
            style={{ borderBottom: '1px solid var(--line)', background: 'var(--accent-soft)' }}
            data-testid="active-search-chip"
          >
            <Search className="h-3.5 w-3.5 flex-none" style={{ color: 'var(--accent)' }} />
            <span className="text-xs truncate flex-1" style={{ color: 'var(--accent-2)', fontWeight: 500 }}>
              «{(listSearch || '').trim()}»
              {useServerSearch && (
                <span style={{ fontWeight: 400, opacity: 0.8 }}>
                  {` · ${alcanceBusqueda(listSearch, { profunda: deepSearch, buscando: isFetching })}`}
                </span>
              )}
            </span>
            {/* Ya no hay botón: el contenido se busca solo. Solo se informa de
                que la parte lenta sigue en marcha. */}
            {useServerSearch && deepSearch && isFetching && (
              <Loader2 className="h-3.5 w-3.5 od-spin flex-none" style={{ color: 'var(--accent)' }} data-testid="deep-search" />
            )}
            <button type="button" className="od-tool-btn !w-7 !h-7" onClick={saveSearch} data-testid="save-search" aria-label="Guardar búsqueda">
              <BookmarkPlus className="h-3.5 w-3.5" />
            </button>
            <button type="button" className="od-tool-btn !w-7 !h-7" onClick={() => setListSearch('')} aria-label="Limpiar búsqueda">
              <XIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Falló un refresco pero la lista ya estaba cargada: se queda a la vista y solo se avisa (M7). */}
        {isError && allMessages.length > 0 && (
          <div
            role="status"
            data-testid="list-error-refresco"
            className="shrink-0 flex items-center gap-2 px-4 py-2"
            style={{ fontSize: 13, background: 'var(--danger-soft)', color: 'var(--text)', borderBottom: '1px solid var(--line)' }}
          >
            <AlertTriangle className="h-4 w-4 flex-none" style={{ color: 'var(--danger)' }} />
            <span className="flex-1 min-w-0">No se pudo actualizar: {describirErrorRed(error).titulo.toLowerCase()}. Ves lo último que se cargó.</span>
            <button
              type="button"
              onClick={() => refetch()}
              style={{ background: 'none', border: 0, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', minHeight: 32, fontFamily: 'inherit' }}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* ---------- Cuerpo ---------- */}
        <div className="flex-1 min-h-0">
          {isError && !allMessages.length ? (
            <div className="p-4">
              <div
                className="flex gap-3.5 p-4"
                style={{ borderRadius: 14, background: 'var(--danger-soft)', border: '1px solid var(--line)' }}
                data-testid="list-error"
              >
                <AlertTriangle className="h-5 w-5 flex-none" style={{ color: 'var(--danger)' }} />
                <div className="flex-1">
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
                    {describirErrorRed(error).titulo}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
                    {describirErrorRed(error).texto}
                  </div>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    style={{
                      marginTop: 12, padding: '8px 16px', borderRadius: 10,
                      border: '1px solid var(--danger)', background: 'transparent',
                      color: 'var(--danger)', fontWeight: 600, fontSize: 13,
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Reintentar sincronización
                  </button>
                </div>
              </div>
            </div>
          ) : showSkeleton ? (
            <div className="py-1" data-testid="list-skeleton">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex gap-3 items-center px-4 py-3.5"
                  style={{ borderBottom: '1px solid var(--line)' }}
                >
                  <div className="flex-none" style={{ width: 16, height: 16, borderRadius: 4, background: 'var(--hover)' }} />
                  <div className="flex-none" style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--hover)' }} />
                  <div className="flex-1 flex flex-col gap-2">
                    <div style={{ height: 10, width: `${45 + (i % 4) * 12}%`, borderRadius: 5, background: 'var(--hover)' }} />
                    <div style={{ height: 9, width: `${60 + (i % 3) * 10}%`, borderRadius: 5, background: 'var(--hover)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : !rows.length && !online ? (
            <EmptyState
              icon={CloudOff}
              tone="panel"
              title="Sin conexión"
              action={<button type="button" className="od-btn-primary" onClick={() => refetch()}>Reintentar</button>}
            >
              No hay conexión a internet. Se sincronizará al recuperarla.
            </EmptyState>
          ) : !rows.length && searching ? (
            <EmptyState icon={Search} tone="panel" title="No se encontraron correos">
              No hay resultados para «{(listSearch || '').trim()}». Prueba con otras palabras o ajusta los filtros.
            </EmptyState>
          ) : !rows.length ? (
            <EmptyState
              icon={isScheduledFolder ? CalendarClock : isSnoozedFolder ? Clock : isStarredFolder ? Star : Inbox}
              tone="accent"
              title={
                isScheduledFolder ? 'Sin envíos programados'
                  : isSnoozedFolder ? 'Nada pospuesto'
                    : isStarredFolder ? 'Sin destacados'
                      : isNeedsReplyFolder ? 'Nada por responder'
                        : vacioPestana ? vacioPestana.titulo
                          : 'Bandeja vacía'
              }
            >
              {isScheduledFolder
                ? 'En el redactor, usa el reloj para programar un envío.'
                : isSnoozedFolder
                  ? 'Usa el reloj al leer un correo para posponerlo.'
                  : isStarredFolder
                    ? 'Marca correos con la estrella para verlos aquí.'
                    : isNeedsReplyFolder
                      ? 'No tienes conversaciones pendientes de responder.'
                      : vacioPestana ? vacioPestana.texto
                        : `No hay correos en ${folderTitle}. Cuando lleguen mensajes nuevos aparecerán aquí.`}
            </EmptyState>
          ) : isScheduledFolder ? (
            <div className="od-scroll h-full" data-testid="scheduled-list">
              {rows.map((m) => (
                <div
                  key={m.scheduledId || m.uid}
                  className="px-3 py-3 sm:px-4"
                  style={{ borderBottom: '1px solid var(--line)' }}
                  data-testid="scheduled-row"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                      style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                      <CalendarClock className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                          {m.subject}
                        </div>
                        <span
                          className="od-chip"
                          style={
                            m.scheduleStatus === 'failed'
                              ? { background: 'var(--danger-soft)', color: 'var(--danger)' }
                              : m.scheduleStatus === 'sent'
                                ? { background: 'var(--good-soft)', color: 'var(--good)' }
                                : { background: 'var(--accent-soft)', color: 'var(--accent)' }
                          }
                        >
                          {ESTADO_PROGRAMADO[m.scheduleStatus] || 'Programado'}
                        </span>
                      </div>
                      <div className="text-sm truncate" style={{ color: 'var(--text-2)' }}>
                        Para: {(m.to || []).join(', ') || m.from?.name || '—'}
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                        {m.date ? `Sale ${textoProgramado(new Date(m.date))}` : '—'}
                        {m.scheduleAttempts > 0 && ` · reintentos ${m.scheduleAttempts}/${m.scheduleMax}`}
                      </div>
                      {m.scheduleError && (
                        <div className="text-xs mt-1 truncate" style={{ color: 'var(--danger)' }}>{m.scheduleError}</div>
                      )}
                      {['scheduled', 'failed', 'retry'].includes(m.scheduleStatus) && (
                        <div className="flex flex-wrap gap-2 mt-2.5">
                          <button type="button" className="od-btn-primary" onClick={() => sendScheduledNow(m)} data-testid="scheduled-send-now">
                            <Send className="h-3.5 w-3.5" />
                            Enviar ahora
                          </button>
                          <button type="button" className="od-btn-secondary" onClick={() => setCancelarProgramado(m)} data-testid="scheduled-cancel">
                            <XIcon className="h-3.5 w-3.5" />
                            Cancelar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : isSnoozedFolder ? (
            <div className="od-scroll h-full" data-testid="snoozed-list">
              {rows.map((m) => (
                <div
                  key={m.snoozeId || `${m.accountId}|${m.folder || activeFolder}|${m.uid}${m._child ? '|hijo' : ''}`}
                  className="px-3 py-3 sm:px-4"
                  style={{ borderBottom: '1px solid var(--line)' }}
                  data-testid="snooze-row"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                      style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                      <Clock className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                        {m.from?.name || m.from?.email || 'Remitente'}
                      </div>
                      <div className="text-sm truncate" style={{ color: 'var(--text-2)' }}>{m.subject}</div>
                      <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                        Vuelve {m.snoozeUntil ? textoProgramado(new Date(m.snoozeUntil)) : '—'}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        <button type="button" className="od-btn-primary" onClick={() => wakeSnooze(m, { open: true })} data-testid="snooze-wake">
                          <AlarmClock className="h-3.5 w-3.5" />
                          Despertar ya
                        </button>
                        <button type="button" className="od-btn-secondary" onClick={() => rescheduleSnooze(m, 24)} data-testid="snooze-again-1d">
                          <TimerReset className="h-3.5 w-3.5" />
                          +1 día
                        </button>
                        <button type="button" className="od-btn-secondary" onClick={() => rescheduleSnooze(m, 3)} data-testid="snooze-again-3h">
                          +3 h
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Virtuoso
              data={rows}
              data-testid="message-list"
              className="od-scroll"
              endReached={() => {
                if (hasNextPage && !isFetchingNextPage) fetchNextPage();
              }}
              components={{
                Footer: () => (isFetchingNextPage ? (
                  <div className="py-3 grid place-items-center">
                    <Loader2 className="h-4 w-4 od-spin" style={{ color: 'var(--accent)' }} />
                  </div>
                ) : null),
              }}
              itemContent={(index, m) => {
                const key = claveFila(m);
                return (
                  <div>
                    {m._group && (
                      <div
                        className="px-4 py-1.5 sticky top-0 z-10"
                        style={{
                          background: 'var(--list)', color: 'var(--text-3)',
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '.06em', borderBottom: '1px solid var(--line)',
                        }}
                      >
                        {m._group}
                      </div>
                    )}
                    <MessageRow
                      m={m}
                      // El mismo uid puede estar en otra cuenta o carpeta (B5): se comparan las tres cosas.
                      selected={selectedUid === m.uid && !m._isThread
                        && (!selectedAccountId || selectedAccountId === m.accountId)
                        && (!selectedFolder || selectedFolder === (m.folder || activeFolder))}
                      expanded={m._isThread && expanded.has(m._threadKey)}
                      mobile={mobile}
                      showAccount={showAccount}
                      mostrarArchivado={activeFolder === 'starred' || activeFolder === 'all'}
                      internalDomains={internalDomains}
                      onClick={() => onRowClick(m)}
                      onAction={(type, e) => action(m, type, e)}
                      definitivo={(m.folder || activeFolder) === 'trash'}
                      checked={checked.has(key)}
                      onCheck={(v) => toggleCheck(key, v)}
                    />
                  </div>
                );
              }}
            />
          )}
        </div>
        <ConfirmarBorradoDefinitivo pendiente={borrado} onCerrar={() => setBorrado(null)} />
        <DialogoConfirmar
          open={!!cancelarProgramado}
          titulo="¿Cancelar el envío programado?"
          mensaje={`«${cancelarProgramado?.subject || '(sin asunto)'}» no se enviará, y desde aquí no se puede recuperar.`}
          boton="Cancelar envío"
          testId="confirmar-cancelar-programado"
          onOpenChange={(v) => !v && setCancelarProgramado(null)}
          onConfirmar={() => { const m = cancelarProgramado; setCancelarProgramado(null); if (m) cancelScheduled(m); }}
        />
        <DialogoConfirmar
          open={!!confirmarMasiva}
          titulo={confirmarMasiva ? {
            archive: `¿Archivar ${confirmarMasiva.n} correos?`,
            trash: `¿Mover ${confirmarMasiva.n} correos a la papelera?`,
            spam: `¿Marcar ${confirmarMasiva.n} correos como spam?`,
          }[confirmarMasiva.kind] : ''}
          mensaje={`Con tantos correos a la vez no se ofrece «Deshacer»: para recuperarlos tendrías que buscarlos en ${
            confirmarMasiva?.kind === 'archive' ? 'Archivo' : confirmarMasiva?.kind === 'spam' ? 'Spam' : 'la Papelera'} y moverlos de vuelta.`}
          boton={confirmarMasiva?.kind === 'archive' ? 'Archivar' : confirmarMasiva?.kind === 'spam' ? 'Marcar como spam' : 'Mover a la papelera'}
          testId="confirmar-accion-masiva"
          onOpenChange={(v) => !v && setConfirmarMasiva(null)}
          onConfirmar={() => { const k = confirmarMasiva?.kind; setConfirmarMasiva(null); if (k) bulkAction(k, true); }}
        />
      </div>
    </TooltipProvider>
  );
}
